import React, {forwardRef, useMemo, useReducer, useRef, useImperativeHandle} from 'react';
import {useSelector} from 'react-redux';
import {FormGroup, Switch, Tabs, Tab, TagInput} from '@blueprintjs/core';

import {ApiStatus} from '../../../models';
import {slackSelector} from '../../../store/reducers/integrationReducer';
import {IState} from '../../../store/reducers';
import {WYSIWYGEditor} from '../../slack';
import JSONEditor from '../../JSONEditor';
import {
  Attachments,
  ContentCard,
  ContentToolbar,
  MessageResult,
  MergePdf,
  NoIntegration,
  NoIntegrationCallout,
  SendWithAllAttachments,
} from '../components';
import {createDefaultContent, initialProps, toBlockKitURL} from './utils';
import {
  useCreateBlock, useExecuteRequest, useGetResult, useInitBlock,
  useUpdateBlockContentJson,
} from '../hooks';
import {BaseBlock, ContentActions} from '../types';
import {defaultContentActions, BLOCK_TYPE} from '../constants';
import {
  aceEditorReadOnly,
  integrationToSelectOption,
  tinyEditorReadOnly,
  toggleAceReadOnly,
  toggleTinyReadOnly
} from '../utils';
import {
  contentReducer,
  setContentFormKeyValue,
  setContentFormKeysValues,
  unsetPreview,
  setPreview,
} from '../store';
import {initialSlackContentState} from './store';
import {MESSAGE_MODE, slackContentOptions} from './constants';
import BlockKitHelper from './BlockKitHelper';
import ThreadTSInput from './ThreadTSInput';
import {SlackContentOptions} from './types';
import {MergeCsv} from "../components/MergeCsv";

import './Slack.scss';

interface SlackProps {
  content: BaseBlock;
  options?: SlackContentOptions;
  actions?: ContentActions;
}

interface MergePdfOption {
  position?: number;
  include?: boolean;
  uid?: string;
}

interface MergePdfConfig {
  enabled: boolean;
  filename: string;
  options: MergePdfOption[];
}

interface MergeCsvOption {
  sheet_name?: string;
  position?: number;
  include?: boolean;
  uid?: string;
}

interface MergeCsvConfig {
  enabled: boolean;
  filename: string;
  options: MergeCsvOption[];
}

export const Slack = forwardRef(({
  content,
  options = slackContentOptions,
  actions = defaultContentActions,
}: SlackProps, ref: any) => {
  const [contentState, contentDispatch] = useReducer(contentReducer, initialSlackContentState);

  options = useMemo((): SlackContentOptions => ({...slackContentOptions, ...options}),
    [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);
  const initProps = useMemo(() => initialProps(), []);

  const aceEditorRef = useRef<any>();
  const tinymceEditorRef = useRef<any>();
  const uid = content.uid;

  const integrations = useSelector(slackSelector) || [];
  const loadingStatus = useSelector((state: IState) => state.integration.loadingStatus);
  const loaded = loadingStatus === ApiStatus.LOADED;
  const noIntegrations = loaded && integrations?.length === 0;

  const {result} = useGetResult(uid, content.result_id, options.loadResult);
  const {executing, executeRequest} = useExecuteRequest(uid);
  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);

  useCreateBlock(content, createDefaultContent());
  useInitBlock(uid, content, contentDispatch);

  useImperativeHandle(ref, () => ({
    getInit() {
      return contentState.init;
    },
    reInitEditor() {
      /*
       * re-init TinymceEditor
       */
      if (contentState.form.editor_mode !== MESSAGE_MODE.WYSIWYG) {
        return;
      }
      if (!tinymceEditorRef.current) {
        return;
      }
      tinymceEditorRef.current.reInitEditor();
    },
    setAttachments(attachments: string[]) {
      /*
       * TODO: remove this and find a better logic to update attachments
       *
       * Attachments can come from an outer source (notebook, report) as they're calculated based on the available
       *   content in the outer source.
       */
      onAttachmentChange(attachments);
    },
    getMergePdfConfig() {
      return contentState.form.merge_pdf_config || {};
    },
    setMergePdfConfig(value: MergePdfConfig) {
      onMergePdfConfigChange(value);
    },
    getMergeCsvConfig() {
      return contentState.form.merge_csv_config || {};
    },
    setMergeCsvConfig(value: MergeCsvConfig) {
      onMergeCsvConfigChange(value);
    },
  }));

  const onExecute = () => {
    const block: any = {
      content_json: {
        channels: contentState.form.channels,
        editor_mode: contentState.form.editor_mode,
        integration_id: contentState.form.integration_id,
        value_markdown: contentState.form.value_markdown,
        value_json: contentState.form.value_json,
        attachments_to_thread: contentState.form.attachments_to_thread,
        attachments: contentState.form.attachments,
        thread_ts: contentState.form.thread_ts,
        send_with_all_attachments: contentState.form.send_with_all_attachments,
        merge_pdf_config: contentState.form.merge_pdf_config,
        merge_csv_config: contentState.form.merge_csv_config,
      }
    };
    executeRequest(block);
  };

  const onAttachmentChange = (attachments: string[]) => {
    contentDispatch(setContentFormKeyValue('attachments', attachments));
    updateBlockContentJson('attachments', attachments);
  }

  const onMergePdfConfigChange = (value: MergePdfConfig) => {
    contentDispatch(setContentFormKeyValue('merge_pdf_config', value));
    updateBlockContentJson('merge_pdf_config', value);
  };

  const onMergeCsvConfigChange = (value: MergeCsvConfig) => {
    contentDispatch(setContentFormKeyValue('merge_csv_config', value));
    updateBlockContentJson('merge_csv_config', value);
  };

  const onIntegrationIdChange = (integrationId: string) => {
    contentDispatch(setContentFormKeyValue('integration_id', integrationId));
    updateBlockContentJson('integration_id', integrationId);
  };

  const onChannelChange = (channels: any) => {
    contentDispatch(setContentFormKeyValue('channels', channels));
    updateBlockContentJson('channels', channels);
  };

  const onAttachmentsToThreadChange = (event: any) => {
    /*
     * Send attachments to thread instead of channel.
     * This requires that a message does exist.
     */
    const checked = event.target.checked;

    contentDispatch(setContentFormKeyValue('attachments_to_thread', checked));
    updateBlockContentJson('attachments_to_thread', checked);
  };

  const onThreadTSChange = (value: string) => {
    contentDispatch(setContentFormKeyValue('thread_ts', value));
    updateBlockContentJson('thread_ts', value);
  };

  const onSendWithAllAttachmentsChange = (value: boolean) => {
    contentDispatch(setContentFormKeyValue('send_with_all_attachments', value));
    updateBlockContentJson('send_with_all_attachments', value);
  };

  const onTabChange = (tabId: MESSAGE_MODE) => {
    contentDispatch(setContentFormKeyValue('editor_mode', tabId));
    updateBlockContentJson('editor_mode', tabId);
  };

  const onMessageChange = (value: any) => {
    if (tinyEditorReadOnly(tinymceEditorRef?.current)) {
      return;
    }

    const keys = ['value_html', 'value_markdown'];
    const values = [value.html, value.markdown];

    contentDispatch(setContentFormKeysValues(keys, values));
    updateBlockContentJson(keys, values);
  };

  const onJSONMessageChange = (value: any) => {
    if (aceEditorReadOnly(aceEditorRef.current?.editor)) {
      return;
    }

    contentDispatch(setContentFormKeyValue('value_json', value));
    updateBlockContentJson('value_json', value);
  };

  const onPreview = (data: any) => {
    const aceEditor = aceEditorRef.current?.editor;
    const tinymceEditor = tinymceEditorRef?.current;

    if (!data) {
      contentDispatch(unsetPreview());

      if (contentState.form.editor_mode === MESSAGE_MODE.WYSIWYG) {
        toggleTinyReadOnly(tinymceEditor, null, false);
      } else if (contentState.form.editor_mode === MESSAGE_MODE.JSON) {
        toggleAceReadOnly(aceEditor, null, false);
      }
    } else {
      try {
        let {value, channels, attachments} = JSON.parse(data);

        contentDispatch(setPreview({
          channels,
          attachments,
        })); // this will take care of channels

        if (contentState.form.editor_mode === MESSAGE_MODE.WYSIWYG) {
          toggleTinyReadOnly(tinymceEditor, value, true);
        } else if (contentState.form.editor_mode === MESSAGE_MODE.JSON) {
          toggleAceReadOnly(aceEditor, value, true);
        }
      } catch (e) {

      }
    }
  };

  return (
    <ContentCard
      key={content.id}
      type={content.type}
      contentId={contentState.init ? uid : null}
      disabled={noIntegrations || options.readOnly}
      noContentElement={noIntegrations ? <NoIntegration contentId={uid as string} type={BLOCK_TYPE.SLACK}/> : null}
      allowCollapse={options.allowCollapse}
      showHeader={options.showHeader}
    >
      <div className="content__slack">
        <ContentToolbar
          contentId={uid as string}
          executing={executing}
          onExecute={options.showRun ? onExecute : null}
          selected={contentState.form.integration_id}
          onSelect={onIntegrationIdChange}
          options={integrations.map(integrationToSelectOption)}
          updating={updating}
          onDelete={actions.onDelete}
          showDelete={options.showDelete}
          showSave={false}
          disabled={options.readOnly}
          showPreview={options.showPreview}
          onPreview={onPreview}
        />
        <div className="content-card__content__form">
          {
            !contentState.form.integration_id ? <NoIntegrationCallout/> : (
              <div className="slack-form">
                <FormGroup label="Channels" className="slack-channels">
                  <TagInput
                    values={contentState.form.channels || []}
                    onChange={onChannelChange}
                    addOnBlur={true}
                    disabled={contentState.isPreview || options.readOnly}
                  />
                </FormGroup>
                {
                  options.showAttachments && (
                    <Attachments
                      value={contentState.form.attachments}
                      onChange={onAttachmentChange}
                      disabled={contentState.isPreview || options.readOnly}
                    />
                  )
                }
                {
                  options.showMergePdf && (
                    <MergePdf
                      config={contentState.form.merge_pdf_config || {}}
                      onConfigChange={onMergePdfConfigChange}
                      attachments={contentState.form.attachments}
                      disabled={contentState.isPreview || options.readOnly}
                    />
                  )
                }
                {
                  options.showMergeCsv && (
                    <MergeCsv
                      config={contentState.form.merge_csv_config || {}}
                      onConfigChange={onMergeCsvConfigChange}
                      attachments={contentState.form.attachments}
                      disabled={contentState.isPreview || options.readOnly}
                    />
                  )
                }
                <FormGroup label="" inline={true}>
                  <Switch
                    defaultChecked={contentState.form.attachments_to_thread || false}
                    onChange={onAttachmentsToThreadChange}
                    disabled={contentState.isPreview || options.readOnly}
                  >
                    Upload attachments in a thread to the main message
                  </Switch>
                </FormGroup>
                {
                  options.showSlackThreadTS && (
                    <ThreadTSInput
                      value={contentState.form.thread_ts}
                      onChange={onThreadTSChange}
                      disabled={contentState.isPreview || options.readOnly || false}
                    />
                  )
                }
                <SendWithAllAttachments
                  value={contentState.form.send_with_all_attachments}
                  onChange={onSendWithAllAttachmentsChange}
                  disabled={
                    contentState.isPreview ||
                    options.readOnly ||
                    (contentState.form.attachments?.length || 0) === 0
                  }
                />
                <FormGroup label="Message" className="slack-message">
                  <Tabs
                    onChange={onTabChange}
                    defaultSelectedTabId={contentState.form.editor_mode}
                    animate={false}
                  >
                    <Tab
                      id={MESSAGE_MODE.WYSIWYG}
                      title="Text Editor"
                      disabled={contentState.isPreview || options.readOnly}
                      panel={
                        <WYSIWYGEditor
                          ref={tinymceEditorRef}
                          defaultValue={content.value_html || ''}
                          onChange={onMessageChange}
                          debounceTime={250}
                          editorProps={initProps}
                          disabled={options.readOnly}
                        />
                      }
                    />
                    <Tab
                      id={MESSAGE_MODE.JSON}
                      title="JSON"
                      className="slack-message__json-editor"
                      disabled={contentState.isPreview || options.readOnly}
                      panel={
                        <>
                          <JSONEditor
                            ref={aceEditorRef}
                            defaultValue={content.value_json || ''}
                            onChange={onJSONMessageChange}
                            debounceTime={400}
                            disabled={options.readOnly}
                          />
                          <BlockKitHelper
                            jsonContent={toBlockKitURL(contentState.form.value_json)}
                          />
                        </>
                      }
                    />
                </Tabs>
              </FormGroup>
              </div>
            )
          }
          <MessageResult
            result={result}
          />
        </div>
      </div>
    </ContentCard>
  );
});
